import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Plus, X, Lock } from "react-feather";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import {
  useGetEmployeeQuery,
  useCreateEmployeeMutation,
  useUpdateEmployeeMutation,
  useReplaceEmployeeSalariesMutation,
  usePromoteEmployeeMutation,
} from "../employeesApi";
import { useGetBranchesQuery } from "../../../admin/adminApi";
import { useGetDepartmentsQuery } from "../../departments/departmentsApi";
import { useGetHrDesignationsQuery } from "../../designations/hrDesignationsApi";
import { useGetDeductionsQuery } from "../../deductions/deductionsApi";
import { useClaims } from "../../../auth/useClaims";
import {
  useCanCreateEmployees,
  useCanEditEmployees,
  useCanPromoteEmployees,
  useCanEditSalary,
} from "../useEmployeePermissions";
import { paths } from "../../../../routes/paths";

const emptyForm = {
  branchId: "",
  empCode: "",
  empIdNo: "",
  empName: "",
  empLastName: "",
  isMale: true,
  empMobile: "",
  empAddress: "",
  empDob: "",
  empDoj: "",
  isPermanent: false,
  empDop: "",
  departmentId: "",
  designationId: "",
  bankCode: "",
  bankBranchCode: "",
  empAcNo: "",
  isActivate: true,
  remarks: "",
  hide: false,
};

const emptySalary = {
  wb: "Wages",
  amount: "0",
  allowance: "0",
  nhif: "",
  nssf: "",
  pin: "",
  hold: false,
  respAllowance: "0",
  vehicleAllowance: "0",
  dailyOT: "0",
};

const emptyPromote = { newEmpCode: "", departmentId: "", designationId: "", reason: "" };

// Toggle-switch styling for every on/off flag on this form (Active,
// Permanent, Hide Salary, Hold Salary) instead of plain checkboxes - same
// pattern already used in BranchAvailabilityPanel.jsx.
const ToggleSwitch = ({ id, checked, onChange, disabled, label }) => (
  <div className="form-check form-switch">
    <input
      type="checkbox"
      className="form-check-input"
      id={id}
      style={{ width: "2.5em", height: "1.4em" }}
      checked={checked}
      onChange={onChange}
      disabled={disabled}
    />
    <label className="form-check-label ms-2" htmlFor={id}>{label}</label>
  </div>
);

// Shared page for Add / Edit / Promote (Texas_Admin/GUI/Master/Frm_Employees.cs
// + Frm_EmployeePromot.cs - handoff §9.6), one component per instruction
// rather than two near-identical screens. Promote is genuinely a different,
// smaller operation than Edit though (its own endpoint, POST /{empId}/promote,
// its own Employees.Promote permission): it re-codes the employee - the old
// empId is deactivated and a brand-new one is created under a new emp code,
// carrying over identity fields automatically server-side. So identity
// fields (name/DOB/address/bank/etc.) are shown as a read-only summary in
// Promote mode, not an editable-but-locked form - there's nothing to submit
// for them, the server copies them across on its own. Only New Employee
// Code / Department / Designation / Reason are actually sent, plus an
// optional follow-up salary call against the newly-created empId (salary is
// never carried over by the promote endpoint itself).
const EmployeeForm = ({ mode }) => {
  const { empId } = useParams();
  const navigate = useNavigate();
  const isEditing = mode !== "new";
  const isPromotion = mode === "promote";
  const canCreate = useCanCreateEmployees();
  const canEdit = useCanEditEmployees();
  const canPromote = useCanPromoteEmployees();
  const canEditSalary = useCanEditSalary();
  const { branches: callerBranchCodes, isAllBranches } = useClaims();
  const { data: branches = [] } = useGetBranchesQuery();
  const { data: departments = [] } = useGetDepartmentsQuery();
  const { data: designations = [] } = useGetHrDesignationsQuery();
  const { data: allDeductions = [] } = useGetDeductionsQuery();
  const {
    data: detail,
    isLoading: isLoadingDetail,
    isError: isDetailError,
    error: detailError,
  } = useGetEmployeeQuery(empId, { skip: !isEditing });
  const [createEmployee, { isLoading: isCreating }] = useCreateEmployeeMutation();
  const [updateEmployee, { isLoading: isUpdating }] = useUpdateEmployeeMutation();
  const [replaceSalaries, { isLoading: isSavingSalary }] = useReplaceEmployeeSalariesMutation();
  const [promoteEmployee, { isLoading: isPromoting }] = usePromoteEmployeeMutation();

  const availableBranches = isAllBranches
    ? branches
    : branches.filter((b) => callerBranchCodes?.includes(b.branchCode));

  const [form, setForm] = useState(emptyForm);
  const [salary, setSalary] = useState(emptySalary);
  const [deductions, setDeductions] = useState([]);
  const [deductionToAdd, setDeductionToAdd] = useState("");
  const [promote, setPromote] = useState(emptyPromote);

  useEffect(() => {
    if (isEditing && detail?.employee) {
      const emp = detail.employee;
      setForm({
        ...emptyForm,
        ...emp,
        empDob: emp.empDob ? dayjs(emp.empDob).format("YYYY-MM-DD") : "",
        empDoj: emp.empDoj ? dayjs(emp.empDoj).format("YYYY-MM-DD") : "",
        isPermanent: Boolean(emp.empDop),
        empDop: emp.empDop ? dayjs(emp.empDop).format("YYYY-MM-DD") : "",
        isMale: Boolean(emp.isMale),
        isActivate: Boolean(emp.isActivate),
        hide: Boolean(emp.hide),
        remarks: "",
      });
      setPromote({
        newEmpCode: "",
        departmentId: emp.departmentId ? String(emp.departmentId) : "",
        designationId: emp.designationId ? String(emp.designationId) : "",
        reason: "",
      });
      const rows = detail.salaries || [];
      if (rows.length > 0) {
        const [first] = rows;
        setSalary({
          wb: first.wb || "Wages",
          amount: String(first.amount ?? "0"),
          allowance: String(first.allowance ?? "0"),
          nhif: first.nhif || "",
          nssf: first.nssf || "",
          pin: first.pin || "",
          hold: Boolean(first.hold),
          respAllowance: String(first.respAllowance ?? "0"),
          vehicleAllowance: String(first.vehicleAllowance ?? "0"),
          dailyOT: String(first.dailyOT ?? "0"),
        });
        setDeductions(rows.map((r) => r.deductions).filter(Boolean));
      } else {
        setSalary(emptySalary);
        setDeductions([]);
      }
    } else if (!isEditing) {
      setForm({
        ...emptyForm,
        branchId: availableBranches.length === 1 ? availableBranches[0].branchId : "",
      });
      setSalary(emptySalary);
      setDeductions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, detail]);

  const setField = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  const setSalaryField = (field) => (e) =>
    setSalary((prev) => ({ ...prev, [field]: e.target.value }));

  const visibleDesignations = form.departmentId
    ? designations.filter((d) => String(d.departmentId) === String(form.departmentId))
    : designations;
  const visiblePromoteDesignations = promote.departmentId
    ? designations.filter((d) => String(d.departmentId) === String(promote.departmentId))
    : designations;

  const handleDepartmentChange = (e) => {
    const value = e.target.value;
    setForm((prev) => ({
      ...prev,
      departmentId: value,
      designationId: designations.some(
        (d) => String(d.designationCode) === String(prev.designationId) && String(d.departmentId) === value
      )
        ? prev.designationId
        : "",
    }));
  };

  const addDeduction = () => {
    if (!deductionToAdd) return;
    if (deductions.includes(deductionToAdd)) {
      toast.error("Already added");
      return;
    }
    setDeductions((prev) => [...prev, deductionToAdd]);
    setDeductionToAdd("");
  };
  const removeDeduction = (name) => setDeductions((prev) => prev.filter((d) => d !== name));

  const canWrite = mode === "new" ? canCreate : mode === "edit" ? canEdit : canPromote;
  const roleReadOnly = !canWrite;

  const empCodeNumber = Number(form.empCode) || 0;
  const idNoRequired = empCodeNumber >= 1000;

  // Salary is only extra-gated by Employees.EditSalary once the record's
  // hide flag is true (handoff §9.6) - otherwise it's the normal
  // Employees.Edit baseline, same as everything else on this form. In
  // Promote mode the new record inherits the OLD employee's current hide
  // value unchanged, so that's what decides whether the follow-up salary
  // call will be permitted.
  const effectiveHide = isPromotion ? Boolean(detail?.employee?.hide) : form.hide;
  const canWriteSalary = effectiveHide ? canEditSalary : canEdit;
  const salaryLocked = isEditing && !isPromotion && Boolean(detail?.salaryHidden);

  const buildSalaryRows = () => {
    const common = {
      wb: salary.wb,
      amount: Number(salary.amount) || 0,
      allowance: Number(salary.allowance) || 0,
      hold: salary.hold ? 1 : 0,
      nhif: salary.nhif,
      nssf: salary.nssf,
      pin: salary.pin,
      respAllowance: Number(salary.respAllowance) || 0,
      vehicleAllowance: Number(salary.vehicleAllowance) || 0,
      dailyOT: Number(salary.dailyOT) || 0,
      date: dayjs().format("YYYY-MM-DDT00:00:00"),
    };
    const names = deductions.length > 0 ? deductions : [null];
    return names.map((name, i) => ({
      recordGuid: null,
      slno: i + 1,
      deductions: name,
      ...common,
    }));
  };

  const handleSave = async () => {
    if (!form.empName.trim()) {
      toast.error("First name is required");
      return;
    }
    if (!form.empAddress.trim()) {
      toast.error("Address is required");
      return;
    }
    if (!form.empAcNo.trim()) {
      toast.error("Bank account number is required");
      return;
    }
    if (!isEditing && !form.branchId) {
      toast.error("Branch is required");
      return;
    }
    if (idNoRequired && !form.empIdNo.trim()) {
      toast.error("ID number is required for this employee code");
      return;
    }

    const body = {
      branchId: Number(form.branchId),
      empCode: Number(form.empCode) || 0,
      empName: form.empName.trim(),
      empLastName: form.empLastName.trim(),
      empIdNo: form.empIdNo.trim() || null,
      empAddress: form.empAddress.trim(),
      empAcNo: form.empAcNo.trim(),
      empDoj: form.empDoj ? dayjs(form.empDoj).format("YYYY-MM-DDT00:00:00") : null,
      empDob: form.empDob ? dayjs(form.empDob).format("YYYY-MM-DDT00:00:00") : null,
      empDop: form.isPermanent && form.empDop ? dayjs(form.empDop).format("YYYY-MM-DDT00:00:00") : null,
      departmentId: form.departmentId ? Number(form.departmentId) : null,
      designationId: form.designationId ? Number(form.designationId) : null,
      isActivate: form.isActivate ? 1 : 0,
      bankCode: form.bankCode || null,
      bankBranchCode: form.bankBranchCode || null,
      remarks: form.remarks || null,
      empMobile: form.empMobile || null,
      isMale: form.isMale ? 1 : 0,
      hide: form.hide,
    };

    try {
      let targetEmpId = empId;
      if (isEditing) {
        await updateEmployee({ empId, ...body }).unwrap();
      } else {
        const created = await createEmployee(body).unwrap();
        targetEmpId = created.employee.empId;
      }
      if (canWriteSalary && !salaryLocked) {
        await replaceSalaries({ empId: targetEmpId, salaries: buildSalaryRows() }).unwrap();
      }
      toast.success("Employee saved");
      navigate(paths.employeeEdit(targetEmpId), { replace: true });
    } catch (error) {
      toast.error(error?.message || "Could not save employee");
    }
  };

  const handlePromote = async () => {
    if (!promote.newEmpCode.trim()) {
      toast.error("Enter the new employee code");
      return;
    }
    if (!promote.reason.trim()) {
      toast.error("Enter a reason for this promotion");
      return;
    }
    const currentDept = detail?.employee?.departmentId ? String(detail.employee.departmentId) : "";
    const currentDesig = detail?.employee?.designationId ? String(detail.employee.designationId) : "";
    if (promote.departmentId === currentDept && promote.designationId === currentDesig) {
      toast.error("A promotion must change the department or designation");
      return;
    }

    try {
      const result = await promoteEmployee({
        empId,
        newEmpCode: Number(promote.newEmpCode),
        departmentId: promote.departmentId ? Number(promote.departmentId) : null,
        designationId: promote.designationId ? Number(promote.designationId) : null,
        reason: promote.reason.trim(),
      }).unwrap();
      const newEmpId = result.employee.empId;
      if (canWriteSalary) {
        await replaceSalaries({ empId: newEmpId, salaries: buildSalaryRows() }).unwrap();
      }
      toast.success("Employee promoted");
      navigate(paths.employeeEdit(newEmpId), { replace: true });
    } catch (error) {
      toast.error(error?.message || "Could not promote employee");
    }
  };

  if (isEditing && isDetailError) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Link to={paths.employees} className="d-inline-flex align-items-center mb-3">
            <ArrowLeft size={16} className="me-1" /> Back to Employees
          </Link>
          <div className="alert alert-danger">
            Could not load this employee ({detailError?.message || "server error"}).
          </div>
        </div>
      </div>
    );
  }

  if (isEditing && isLoadingDetail) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const title = mode === "new" ? "Add New Employee" : isPromotion ? "Promote Employee" : "Edit Employee";
  const emp = detail?.employee;

  const salaryFields = (
    <div className="card mb-4">
      <div className="card-body">
        <h5 className="mb-3">Salary Details</h5>
        {salaryLocked ? (
          <div className="alert alert-secondary d-flex align-items-center gap-2 mb-0">
            <Lock size={16} />
            Salary details are hidden for this employee. You need the Employees.ViewSalary
            permission to see them.
          </div>
        ) : (
          <div className="row">
            <div className="col-md-3 mb-3">
              <label className="form-label d-block">Payment Type</label>
              <div className="d-flex gap-3" style={{ height: 38, alignItems: "center" }}>
                <div className="form-check mb-0">
                  <input type="radio" className="form-check-input" id="wb-wages" checked={salary.wb === "Wages"} onChange={() => setSalary((p) => ({ ...p, wb: "Wages" }))} disabled={!canWriteSalary} />
                  <label className="form-check-label" htmlFor="wb-wages">Wages</label>
                </div>
                <div className="form-check mb-0">
                  <input type="radio" className="form-check-input" id="wb-basic" checked={salary.wb === "Basic Pay"} onChange={() => setSalary((p) => ({ ...p, wb: "Basic Pay" }))} disabled={!canWriteSalary} />
                  <label className="form-check-label" htmlFor="wb-basic">Basic Pay</label>
                </div>
              </div>
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label">Amount</label>
              <input type="number" className="form-control" value={salary.amount} onChange={setSalaryField("amount")} disabled={!canWriteSalary} />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label">House Allowance</label>
              <input type="number" className="form-control" value={salary.allowance} onChange={setSalaryField("allowance")} disabled={!canWriteSalary} />
            </div>
            <div className="col-md-3 mb-3 d-flex align-items-end">
              <ToggleSwitch
                id="salaryHold"
                checked={salary.hold}
                onChange={(e) => setSalary((p) => ({ ...p, hold: e.target.checked }))}
                disabled={!canWriteSalary}
                label="Hold Salary"
              />
            </div>

            <div className="col-md-12 mb-2">
              <label className="form-label">Deductions</label>
              {canWriteSalary && (
                <div className="d-flex gap-2 mb-2">
                  <select className="form-control" style={{ maxWidth: 260 }} value={deductionToAdd} onChange={(e) => setDeductionToAdd(e.target.value)}>
                    <option value="">--Select a deduction--</option>
                    {allDeductions.map((d) => (
                      <option key={d.deductionId} value={d.deductionName}>{d.deductionName}</option>
                    ))}
                  </select>
                  <button type="button" className="btn btn-outline-primary btn-sm" onClick={addDeduction} disabled={!deductionToAdd}>
                    <Plus size={14} className="me-1" /> Add
                  </button>
                </div>
              )}
              <div className="d-flex flex-wrap gap-2">
                {deductions.length === 0 && <span className="text-muted">No deductions added.</span>}
                {deductions.map((name) => (
                  <span key={name} className="badge bg-light text-dark border d-inline-flex align-items-center gap-1 p-2">
                    {name}
                    {canWriteSalary && (
                      <button type="button" className="btn btn-link p-0 text-danger" onClick={() => removeDeduction(name)}>
                        <X size={12} />
                      </button>
                    )}
                  </span>
                ))}
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <label className="form-label">NHIF No</label>
              <input type="text" className="form-control" value={salary.nhif} onChange={setSalaryField("nhif")} disabled={!canWriteSalary} />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label">NSSF No</label>
              <input type="text" className="form-control" value={salary.nssf} onChange={setSalaryField("nssf")} disabled={!canWriteSalary} />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label">PIN</label>
              <input type="text" className="form-control" value={salary.pin} onChange={setSalaryField("pin")} disabled={!canWriteSalary} />
            </div>

            {/* Not applicable to every employee, so pushed to their own last
                row rather than sitting alongside the core pay fields above. */}
            <div className="col-md-4 mb-3">
              <label className="form-label">Responsibility Allowance</label>
              <input type="number" className="form-control" value={salary.respAllowance} onChange={setSalaryField("respAllowance")} disabled={!canWriteSalary} />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label">Vehicle Allowance</label>
              <input type="number" className="form-control" value={salary.vehicleAllowance} onChange={setSalaryField("vehicleAllowance")} disabled={!canWriteSalary} />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label">Daily OT</label>
              <input type="number" className="form-control" value={salary.dailyOT} onChange={setSalaryField("dailyOT")} disabled={!canWriteSalary} />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (isPromotion) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <Link to={paths.employees} className="d-inline-flex align-items-center mb-2">
                  <ArrowLeft size={16} className="me-1" /> Back to Employees
                </Link>
                <h4>{title}</h4>
              </div>
            </div>
          </div>

          {!canPromote && (
            <div className="alert alert-info">
              You have read-only access. Promoting employees requires the Employees.Promote permission.
            </div>
          )}

          <div className="card mb-4">
            <div className="card-body">
              <h5 className="mb-3">Promoting</h5>
              <p className="text-muted small">
                Identity details carry over automatically - only what changes for the new role is
                entered here.
              </p>
              <div className="row">
                <div className="col-md-3 mb-2"><strong>Name</strong><div>{emp?.empName} {emp?.empLastName}</div></div>
                <div className="col-md-3 mb-2"><strong>Current Code</strong><div>{emp?.empCode}</div></div>
                <div className="col-md-3 mb-2"><strong>Department</strong><div>{emp?.departmentName || "-"}</div></div>
                <div className="col-md-3 mb-2"><strong>Designation</strong><div>{emp?.designationName || "-"}</div></div>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <h5 className="mb-3">New Role</h5>
              <div className="row">
                <div className="col-md-3 mb-3">
                  <label className="form-label">New Employee Code</label>
                  <input type="number" className="form-control" value={promote.newEmpCode} onChange={(e) => setPromote((p) => ({ ...p, newEmpCode: e.target.value }))} disabled={!canPromote} />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Department</label>
                  <select
                    className="form-control"
                    value={promote.departmentId}
                    onChange={(e) => setPromote((p) => ({
                      ...p,
                      departmentId: e.target.value,
                      designationId: designations.some((d) => String(d.designationCode) === String(p.designationId) && String(d.departmentId) === e.target.value) ? p.designationId : "",
                    }))}
                    disabled={!canPromote}
                  >
                    <option value="">--Select--</option>
                    {departments.map((d) => (
                      <option key={d.departmentCode} value={d.departmentCode}>{d.departmentName}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Designation</label>
                  <select className="form-control" value={promote.designationId} onChange={(e) => setPromote((p) => ({ ...p, designationId: e.target.value }))} disabled={!canPromote}>
                    <option value="">--Select--</option>
                    {visiblePromoteDesignations.map((d) => (
                      <option key={d.designationCode} value={d.designationCode}>{d.designationName}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-12 mb-3">
                  <label className="form-label">Promotion Reason <span className="text-danger">*</span></label>
                  <input type="text" className="form-control" value={promote.reason} onChange={(e) => setPromote((p) => ({ ...p, reason: e.target.value }))} disabled={!canPromote} placeholder="e.g. Confirmed to permanent staff, new designation" />
                </div>
              </div>
            </div>
          </div>

          {salaryFields}

          {canPromote && (
            <div className="d-flex gap-2 mb-4">
              <button type="button" className="btn btn-primary" onClick={handlePromote} disabled={isPromoting || isSavingSalary}>
                Promote
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => navigate(paths.employees)}>
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  const isSaving = isCreating || isUpdating || isSavingSalary;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <Link to={paths.employees} className="d-inline-flex align-items-center mb-2">
                <ArrowLeft size={16} className="me-1" /> Back to Employees
              </Link>
              <h4>{title}</h4>
              {isEditing && <h6>{form.empName} {form.empLastName} · Code {form.empCode}</h6>}
            </div>
          </div>
        </div>

        {!canWrite && (
          <div className="alert alert-info">
            You have read-only access. {isEditing ? "Editing" : "Creating"} employees requires the
            Employees.{isEditing ? "Edit" : "Create"} permission.
          </div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <h5 className="mb-3">Employee Details</h5>
            <div className="row">
              <div className="col-md-3 mb-3">
                <label className="form-label">Branch</label>
                {isEditing ? (
                  <input
                    type="text"
                    className="form-control"
                    value={form.branchCode || branches.find((b) => b.branchId === form.branchId)?.branchCode || ""}
                    readOnly
                    disabled
                  />
                ) : (
                  <select className="form-control" value={form.branchId} onChange={setField("branchId")} disabled={roleReadOnly}>
                    <option value="">--Select--</option>
                    {availableBranches.map((b) => (
                      <option key={b.branchId} value={b.branchId}>{b.branchCode}</option>
                    ))}
                  </select>
                )}
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">Employee Code</label>
                <input type="number" className="form-control" value={form.empCode} onChange={setField("empCode")} disabled={roleReadOnly} />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">
                  ID Number{idNoRequired && <span className="text-danger"> *</span>}
                </label>
                <input type="text" className="form-control" value={form.empIdNo} onChange={setField("empIdNo")} disabled={roleReadOnly} />
              </div>
              <div className="col-md-3 mb-3 d-flex align-items-end">
                <ToggleSwitch
                  id="isActivate"
                  checked={form.isActivate}
                  onChange={(e) => setForm((prev) => ({ ...prev, isActivate: e.target.checked }))}
                  disabled={roleReadOnly}
                  label="Active"
                />
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">First Name</label>
                <input type="text" className="form-control" value={form.empName} onChange={setField("empName")} disabled={roleReadOnly} />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Last Name</label>
                <input type="text" className="form-control" value={form.empLastName} onChange={setField("empLastName")} disabled={roleReadOnly} />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label d-block">Gender</label>
                <div className="d-flex gap-4" style={{ height: 38, alignItems: "center" }}>
                  <div className="form-check mb-0">
                    <input type="radio" className="form-check-input" id="gender-male" checked={form.isMale} onChange={() => setForm((p) => ({ ...p, isMale: true }))} disabled={roleReadOnly} />
                    <label className="form-check-label" htmlFor="gender-male">Male</label>
                  </div>
                  <div className="form-check mb-0">
                    <input type="radio" className="form-check-input" id="gender-female" checked={!form.isMale} onChange={() => setForm((p) => ({ ...p, isMale: false }))} disabled={roleReadOnly} />
                    <label className="form-check-label" htmlFor="gender-female">Female</label>
                  </div>
                </div>
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">Mobile</label>
                <input type="text" className="form-control" value={form.empMobile} onChange={setField("empMobile")} disabled={roleReadOnly} />
              </div>
              <div className="col-md-8 mb-3">
                <label className="form-label">Address</label>
                <input type="text" className="form-control" value={form.empAddress} onChange={setField("empAddress")} disabled={roleReadOnly} />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Date of Birth</label>
                <input type="date" className="form-control" value={form.empDob} onChange={setField("empDob")} disabled={roleReadOnly} />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Date of Joining</label>
                <input type="date" className="form-control" value={form.empDoj} onChange={setField("empDoj")} disabled={roleReadOnly} />
              </div>

              {/* Department/Designation are correlated - the Designation
                  dropdown is filtered to the selected Department's own
                  designations (visibleDesignations), sourced live from the
                  Department/HR-Designation master tables. */}
              <div className="col-md-4 mb-3">
                <label className="form-label">Department</label>
                <select className="form-control" value={form.departmentId} onChange={handleDepartmentChange} disabled={roleReadOnly}>
                  <option value="">--Select--</option>
                  {departments.map((d) => (
                    <option key={d.departmentCode} value={d.departmentCode}>{d.departmentName}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Designation</label>
                <select className="form-control" value={form.designationId} onChange={setField("designationId")} disabled={roleReadOnly}>
                  <option value="">--Select--</option>
                  {visibleDesignations.map((d) => (
                    <option key={d.designationCode} value={d.designationCode}>{d.designationName}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-4 mb-3">
                {/* Date Made Permanent lives inside this same cell, stacked
                    below the toggle, instead of appearing in an earlier row
                    - toggling only grows this one cell, it never shifts
                    Department/Designation or any other field. */}
                <label className="form-label d-block">&nbsp;</label>
                <ToggleSwitch
                  id="isPermanent"
                  checked={form.isPermanent}
                  onChange={(e) => setForm((prev) => ({ ...prev, isPermanent: e.target.checked }))}
                  disabled={roleReadOnly}
                  label={form.isPermanent ? "Permanent Staff" : "Casual Staff"}
                />
                {form.isPermanent && (
                  <div className="mt-2">
                    <label className="form-label">Date Made Permanent</label>
                    <input type="date" className="form-control" value={form.empDop} onChange={setField("empDop")} disabled={roleReadOnly} />
                  </div>
                )}
              </div>

              <div className="col-md-12 mb-3">
                <label className="form-label">Remarks</label>
                <input type="text" className="form-control" value={form.remarks} onChange={setField("remarks")} disabled={roleReadOnly} />
              </div>
            </div>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-body">
            <h5 className="mb-3">Bank Details</h5>
            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">Bank Code</label>
                <input type="text" className="form-control" value={form.bankCode} onChange={setField("bankCode")} disabled={roleReadOnly} />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Bank Branch Code</label>
                <input type="text" className="form-control" value={form.bankBranchCode} onChange={setField("bankBranchCode")} disabled={roleReadOnly} />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Bank Account No</label>
                <input type="text" className="form-control" value={form.empAcNo} onChange={setField("empAcNo")} disabled={roleReadOnly} />
              </div>
              <div className="col-md-12 d-flex align-items-center">
                <ToggleSwitch
                  id="hideSalary"
                  checked={form.hide}
                  onChange={(e) => setForm((prev) => ({ ...prev, hide: e.target.checked }))}
                  disabled={!canEditSalary}
                  label="Hide salary from other users"
                />
              </div>
            </div>
          </div>
        </div>

        {salaryFields}

        {canWrite && (
          <div className="d-flex gap-2 mb-4">
            <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
              Save
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => navigate(paths.employees)}>
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeForm;
